import { kCustomConstructionWorkItemCategoryId } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/usePredefinedConstructionItems';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { CustomWorkItemBottomSheetModal } from '@euler/app/flows/task/pages/construction/components/CustomWorkItemBottomSheetModal';
import { kDefaultConstructionWorkItemCategoryId } from '@euler/app/flows/task/pages/construction/functions/useConstructionWorkItems';
import { WiredTaskDetailPageContainer } from '@euler/app/flows/task/pages/detail/components/DetailPageContainer';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  kConstructionJobContextVersion,
  ScheduleConstructionJobPayload,
} from '@euler/model/task-detail';
import { showAlert } from '@euler/utils';
import { array2map } from '@euler/utils/array';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Subject } from 'rxjs';
import {
  SelectedWorkItemsBottomSheet,
  WorkItemsBottomSheetAction,
} from './components/WorkItemsBottomSheet';
import { WorkItemTabBar } from './components/WorkItemTabBar';
import {
  useConstructionJobScheduleContext,
  useScheduleConstructionJobs,
} from './ConstructionJobScheduleContext';
import { ScheduleConstructionJobNavParams } from './ScheduleConstructionJobFlow';
import { WorkItemsTabContentScreen } from './WorkItemsTabContentScreen';

const Tab = createMaterialTopTabNavigator();

export const ScheduleJobsScreen = wrapNavigatorScreen(
  () => {
    const navigation =
      useNavigation<StackNavigationProp<ScheduleConstructionJobNavParams>>();

    const isMobileLayout = useIsMobileLayout();
    const appLoading = useAppLoading();
    const confirm = useConfirm();

    const { rpx } = useResponsiveContext();
    const { taskManager } = useTaskContext();
    const { categories$ } = useConstructionJobScheduleContext();
    const { selectedWorkItems } = useScheduleConstructionJobs();

    const [categories] = useBehaviorSubject(categories$);

    const workItemsModalAction$ = useCreation(
      () => new Subject<WorkItemsBottomSheetAction>(),
      [],
    );

    const key = categories
      .filter(x => x.id !== kCustomConstructionWorkItemCategoryId)
      .map(x => x.id)
      .join(':');

    const customCategory = categories.find(
      x => x.id === kCustomConstructionWorkItemCategoryId,
    );

    const renderContent = useCallback(
      ({ paddingTop }: { paddingTop: number }) => {
        if (!key) {
          return <View />;
        }
        return (
          <View
            css={`
              flex: 1;
              padding-top: ${paddingTop}px;
            `}
          >
            <Tab.Navigator
              tabBar={props => <WorkItemTabBar {...props} />}
              key={key}
              screenOptions={{
                lazy: true,
              }}
              style={{ flex: 1 }}
            >
              {categories
                .filter(x => x.id !== kCustomConstructionWorkItemCategoryId)
                .map(category => (
                  <Tab.Screen
                    key={category.name}
                    name={category.name}
                    component={WorkItemsTabContentScreen as any}
                    initialParams={{ categories$, id: category.id }}
                  />
                ))}
              {customCategory != null && (
                <Tab.Screen
                  name={customCategory.name}
                  component={WorkItemsTabContentScreen as any}
                  initialParams={{ categories$, id: customCategory.id }}
                />
              )}
            </Tab.Navigator>
          </View>
        );
      },
      [categories, categories$, customCategory, key],
    );

    const onSearchPress = usePersistFn(() => {
      navigation.navigate('_workItemSearch', {});
    });

    const [showCustomWorkItemModal, setShowCustomWorkItemModal] =
      useState(false);

    const onAddPress = usePersistFn(() => {
      setShowCustomWorkItemModal(true);
    });

    const done = usePersistFn(async () => {
      try {
        appLoading.show();
        const payloads: ScheduleConstructionJobPayload[] = [];
        const categoryMap = array2map(categories, x => x.id);
        const defaultCategory = categoryMap.get(
          kDefaultConstructionWorkItemCategoryId,
        )!;
        for (const { workItem, assignee } of selectedWorkItems) {
          const category = categoryMap.get(workItem.categoryId);
          payloads.push({
            name: workItem.name,
            workItems: workItem.details,
            assignee,
            context: {
              version: kConstructionJobContextVersion,
              category: category?.name ?? defaultCategory.name,
              resolved: workItem.relatedFaultLikeSubjects,
              procedures:
                workItem.procedures?.map(x => ({
                  name: x.name,
                  type: x.type,
                  removable: x.removable,
                  allowChooseMediaFromGallery: x.allowChooseMediaFromGallery,
                })) ?? [],
            },
          });
        }
        await taskManager.constructionManager.scheduleJobs(payloads);
        appLoading.hide();
        navigation.goBack();
      } catch (e) {
        appLoading.hide();
        void showAlert('出错了', (e as Error).message);
      }
    });

    const onDone = usePersistFn(async () => {
      if (selectedWorkItems.some(x => !x.assignee)) {
        if (
          await confirm({
            title: '提示',
            message: '有施工项目尚未派工，建议您为施工项目派工，是否现在派工? ',
          })
        ) {
          workItemsModalAction$.next({ type: 'reveal' });
          return;
        }
      }
      await done();
    });

    const renderSearchButton = useCallback(() => {
      return (
        <HStack>
          <TouchableOpacity
            hitSlop={PredefinedHitSlops.large}
            onPress={onSearchPress}
            css={`
              width: ${rpx(26)}px;
              height: ${rpx(32)}px;
              align-items: center;
              justify-content: center;
            `}
          >
            <Icon
              name="search"
              size={rpx(20)}
              color="rgba(255, 255, 255, 0.9)"
              css={`
                top: ${rpx(1.5)}px;
              `}
            />
          </TouchableOpacity>
          <TouchableOpacity
            hitSlop={PredefinedHitSlops.large}
            onPress={onAddPress}
            css={`
              height: ${rpx(32)}px;
              margin-left: ${rpx(8)}px;
              flex-direction: row;
              align-items: center;
              justify-content: center;
            `}
          >
            <Icon
              name="plus-circle"
              size={rpx(24)}
              color="rgba(255, 255, 255, 0.9)"
            />
          </TouchableOpacity>
        </HStack>
      );
    }, [onAddPress, onSearchPress, rpx]);

    return (
      <>
        <WiredTaskDetailPageContainer
          title="管理施工项目"
          renderContent={renderContent}
          noDetailHeader={!isMobileLayout}
          navBarRightOptions={renderSearchButton}
          contentContainerStyle={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
        />
        <SelectedWorkItemsBottomSheet
          action$={workItemsModalAction$}
          onDone={onDone}
        />
        <CustomWorkItemBottomSheetModal
          visible={showCustomWorkItemModal}
          onDismiss={() => setShowCustomWorkItemModal(false)}
        />
      </>
    );
  },
  {
    headerShown: false,
  },
);
